import { useState, useEffect } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAuthStore } from '../../stores/auth.store';
import { usersService } from '../../services/users.service';
import api, { ApiResponse } from '../../services/api';
import ReportDefaultsForm from '../../components/ReportDefaultsForm';
import type { TrackingDefaults } from '../../utils/user-settings';
import AppearanceField from '../../components/AppearanceField';
import type { ThemeChoice } from '../../hooks/useThemeMode';
import { BirthdayField } from '../../components/BirthdayField';
import PresetManagerDialog from '../tracking/PresetManagerDialog';
import { useTrackingPresets } from '../../hooks/useTrackingPresets';
import { labelForField } from '../tracking/tracking-page.format';

// No live report here, so group labels fall back to the static catalogue (no v-name renames).
const presetLabel = labelForField;

/** Self-service: the admin editor needs the user list, which non-admins cannot load. */
export default function PreferencesPanel() {
  const user = useAuthStore((s) => s.user);
  const setUserSettings = useAuthStore((s) => s.setUserSettings);
  const { enqueueSnackbar } = useSnackbar();

  const setUserBirthday = useAuthStore((s) => s.setUserBirthday);
  const [draft, setDraft] = useState<TrackingDefaults>(() => ({ ...user?.settings?.tracking }));
  const [theme, setTheme] = useState<ThemeChoice>(() => user?.settings?.theme_mode ?? 'system');
  const [birthday, setBirthday] = useState('');
  const [saving, setSaving] = useState(false);
  const { presets, savePresets } = useTrackingPresets();
  const [managePresets, setManagePresets] = useState(false);

  // The login payload doesn't carry the birthday, so read the current value from /auth/me.
  useEffect(() => {
    let cancelled = false;
    api.get<ApiResponse<{ birthday: string | null }>>('/auth/me')
      .then((res) => { if (!cancelled) setBirthday(res.data.data?.birthday ?? ''); })
      .catch(() => { /* leave the field empty */ });
    return () => { cancelled = true; };
  }, []);

  const set = <K extends keyof TrackingDefaults>(key: K, v: TrackingDefaults[K] | undefined) => {
    setDraft((prev) => {
      const next = { ...prev };
      // "No preference" is an absent key, not a placeholder value.
      if (v === undefined) delete next[key];
      else next[key] = v;
      return next;
    });
  };

  const save = async () => {
    const current = useAuthStore.getState().user;
    if (!current?.id) return;
    setSaving(true);
    try {
      // own areas only — per-key merge keeps presets saved elsewhere
      const settings = { theme_mode: theme, tracking: draft };
      const updated = await usersService.update(current.id, { settings });
      setUserSettings(updated.settings || {});
      // Birthday is self-set via /auth/me (not the admin user update).
      const bday = birthday || null;
      await api.patch('/auth/me', { birthday: bday });
      setUserBirthday(bday);
      enqueueSnackbar('Preferences saved', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not save preferences', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What Tracking, Clicks and Conversions open on for you. Leave any field on "Default"
        to use the shared setting.
      </Typography>

      <Box sx={{ mb: 3 }}>
        <AppearanceField value={theme} onChange={setTheme} />
      </Box>

      <Box sx={{ mb: 3 }}>
        <BirthdayField
          value={birthday}
          onChange={setBirthday}
          helperText="We'll throw you a little celebration on the day 🎂 (and let your workspace know)."
          sx={{ width: 220 }}
        />
      </Box>

      <ReportDefaultsForm value={draft} onChange={set} admin={user?.role === 'admin'} />

      <Button variant="contained" onClick={save} disabled={saving} sx={{ mt: 3 }}>
        {saving ? 'Saving…' : 'Save'}
      </Button>

      <Divider sx={{ my: 3 }} />
      <Typography variant="subtitle2" fontWeight={600}>Presets</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Rename, reorder, delete or edit your saved views. Create new ones from the Tracking page.
      </Typography>
      <Button size="small" variant="outlined" onClick={() => setManagePresets(true)}>
        Manage presets…
      </Button>

      <PresetManagerDialog
        open={managePresets}
        onClose={() => setManagePresets(false)}
        presets={presets}
        vLabel={presetLabel}
        onApply={(next) => savePresets(next).catch(() => enqueueSnackbar('Could not save presets', { variant: 'error' }))}
      />
    </Box>
  );
}
